import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { ArrowLeft, Loader2, Eye, MailCheck, MousePointerClick, AlertTriangle, ShieldAlert, RefreshCw, Search, Copy, FileText, CalendarClock, X } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const STATUS_STYLES = {
  sent:        { label: 'Sent', class: 'bg-slate-100 text-slate-600' },
  delivered:   { label: 'Delivered', class: 'bg-blue-100 text-blue-700' },
  opened:      { label: 'Opened', class: 'bg-emerald-100 text-emerald-700' },
  clicked:     { label: 'Clicked', class: 'bg-purple-100 text-purple-700' },
  bounced:     { label: 'Bounced', class: 'bg-rose-100 text-rose-700' },
  complained:  { label: 'Complaint', class: 'bg-amber-100 text-amber-700' },
};

export default function EmailCampaignDetail() {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [recipTotal, setRecipTotal] = useState(0);
  const [statusFilterCapped, setStatusFilterCapped] = useState(false);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [recipLoading, setRecipLoading] = useState(false);
  const [showBody, setShowBody] = useState(false);
  // iter249: server-side pagination so 1720+ recipient campaigns show
  // every row instead of being capped at the previous client-side 1000.
  const [pageSize, setPageSize] = useState(50);
  const [pageIndex, setPageIndex] = useState(0);
  const navigate = useNavigate();

  const loadCampaign = async () => {
    try {
      const { data } = await api.get(`/email/campaigns/${id}`);
      setCampaign(data);
    } catch (e) {
      toast.error('Failed to load campaign');
    }
  };

  const loadRecipients = async () => {
    setRecipLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(pageSize),
        skip: String(pageIndex * pageSize),
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (filter.trim()) params.set('search', filter.trim());
      const { data } = await api.get(`/email/campaigns/${id}/recipients?${params.toString()}`);
      setRecipients(data.recipients || []);
      setRecipTotal(data.total || 0);
      setStatusFilterCapped(!!data.status_filter_capped);
    } catch {
      // Soft fail — campaign card still shows.
    } finally {
      setRecipLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    await Promise.all([loadCampaign(), loadRecipients()]);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Re-fetch recipients on pagination / filter change. Debounce the
  // email substring filter so we don't hammer the API per keystroke.
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(loadRecipients, filter ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [pageIndex, pageSize, statusFilter, filter]);

  // Reset to page 0 when filter/status changes (server pagination).
  useEffect(() => { setPageIndex(0); /* eslint-disable-next-line */ }, [statusFilter, filter, pageSize]);

  const formatEt = (utcIso) => {
    if (!utcIso) return '';
    try {
      return new Date(utcIso).toLocaleString('en-US', {
        timeZone: 'America/New_York',
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      }) + ' ET';
    } catch { return utcIso; }
  };

  const handleCancelSchedule = async () => {
    if (!window.confirm('Cancel this scheduled send and revert to draft?')) return;
    try {
      await api.post(`/email/campaigns/${id}/cancel-schedule`);
      toast.success('Schedule cancelled — campaign is back to draft');
      await loadCampaign();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel schedule');
    }
  };

  if (loading) return <DashboardLayout title="Email Campaign"><div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div></DashboardLayout>;
  if (!campaign) return <DashboardLayout title="Email Campaign"><div className="p-8 text-slate-500">Campaign not found.</div></DashboardLayout>;

  const counters = [
    { key: 'recipient_count', label: 'Recipients', icon: MailCheck, color: 'text-slate-700' },
    { key: 'delivered_count', label: 'Delivered', icon: MailCheck, color: 'text-blue-700' },
    { key: 'opened_count', label: 'Opens', icon: Eye, color: 'text-emerald-700' },
    { key: 'clicked_count', label: 'Clicks', icon: MousePointerClick, color: 'text-purple-700' },
    { key: 'bounced_count', label: 'Bounces', icon: AlertTriangle, color: 'text-rose-700' },
    { key: 'complained_count', label: 'Complaints', icon: ShieldAlert, color: 'text-amber-700' },
  ];

  const rate = (n, d) => (d > 0 ? `${Math.round((n / d) * 100)}%` : '—');

  const totalPages = Math.max(1, Math.ceil(recipTotal / pageSize));
  const startIdx = recipTotal === 0 ? 0 : pageIndex * pageSize + 1;
  const endIdx = Math.min((pageIndex + 1) * pageSize, recipTotal);

  return (
    <DashboardLayout title={campaign.name || 'Email Campaign'}>
    <div className="p-6 space-y-4" data-testid="campaign-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/admin/email-campaigns"><Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4" /></Button></Link>
          <div>
            <h1 className="text-xl font-semibold">{campaign.name}</h1>
            <p className="text-xs text-slate-500">{campaign.subject}</p>
            <p className="text-xs text-slate-400">
              {campaign.status === 'scheduled' && campaign.scheduled_for
                ? <span className="text-indigo-700 flex items-center gap-1"><CalendarClock className="h-3 w-3" />Scheduled for {formatEt(campaign.scheduled_for)} by {campaign.scheduled_by_name || '—'}</span>
                : campaign.sent_at ? `Sent ${new Date(campaign.sent_at).toLocaleString()} by ${campaign.sent_by_name || '—'}` : 'Not sent'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {campaign.status === 'scheduled' && (
            <Button variant="outline" size="sm" onClick={handleCancelSchedule} className="text-rose-600 border-rose-300 hover:bg-rose-50" data-testid="cancel-schedule-detail-btn">
              <X className="h-3 w-3 mr-1" />Cancel Schedule
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowBody(true)} data-testid="view-body-btn"><FileText className="h-3 w-3 mr-1" />View Body</Button>
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const { data } = await api.post(`/email/campaigns/${id}/duplicate`);
              toast.success(`Duplicated as "${data.name}"`);
              navigate('/admin/email-campaigns');
            } catch (e) { toast.error(e?.response?.data?.detail || 'Duplicate failed'); }
          }} data-testid="duplicate-from-detail-btn"><Copy className="h-3 w-3 mr-1" />Duplicate</Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-campaign-btn"><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-6 gap-3">
        {counters.map(({ key, label, icon: Icon, color }) => (
          <Card key={key}>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-slate-500">{label}</p>
                <Icon className={`h-3.5 w-3.5 ${color}`} />
              </div>
              <p className={`text-2xl font-semibold ${color}`} data-testid={`counter-${key}`}>{campaign[key] || 0}</p>
              {key === 'opened_count' && (
                <p className="text-xs text-slate-400 mt-1">Open rate {rate(campaign.opened_count, campaign.delivered_count || campaign.recipient_count)}</p>
              )}
              {key === 'clicked_count' && (
                <p className="text-xs text-slate-400 mt-1">CTR {rate(campaign.clicked_count, campaign.delivered_count || campaign.recipient_count)}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            Recipients <span className="text-xs font-normal text-slate-500">({recipTotal.toLocaleString()} total{statusFilter !== 'all' ? ` · filtered by ${statusFilter}` : ''})</span>
            {recipLoading && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input className="pl-8" placeholder="Filter by email…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="recipient-filter" />
            </div>
            <div className="flex gap-1 flex-wrap">
              {['all', 'sent', 'delivered', 'opened', 'clicked', 'bounced', 'complained'].map((s) => (
                <Badge
                  key={s}
                  variant={statusFilter === s ? 'default' : 'outline'}
                  className="cursor-pointer capitalize"
                  onClick={() => setStatusFilter(s)}
                  data-testid={`filter-status-${s}`}
                >{s}</Badge>
              ))}
            </div>
          </div>

          {statusFilterCapped && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-2">
              Showing the first 5,000 sends only — status filter on very large campaigns is capped to keep the page fast.
            </div>
          )}

          {recipients.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm" data-testid="no-recipients-msg">
              {recipTotal === 0
                ? (filter || statusFilter !== 'all' ? 'No matches.' : 'No recipient records yet. Webhook events arrive as Resend processes the campaign.')
                : 'No matches on this page.'}
            </div>
          ) : (
            <div className="space-y-1 max-h-[60vh] overflow-y-auto" data-testid="recipients-list">
              {recipients.map((r) => {
                const s = STATUS_STYLES[r.status] || { label: r.status, class: 'bg-slate-100 text-slate-600' };
                return (
                  <div key={r.id} className="flex items-center justify-between p-2 border rounded-md text-xs hover:bg-slate-50" data-testid={`recipient-row-${r.email}`}>
                    <div className="flex-1">
                      <p className="font-medium text-slate-700">{r.email}</p>
                      <p className="text-slate-400">
                        {r.sent_at && `Sent ${new Date(r.sent_at).toLocaleString()} · `}
                        {r.delivered_at && `Delivered ${new Date(r.delivered_at).toLocaleTimeString()} · `}
                        {r.opened_at && `Opened ${new Date(r.opened_at).toLocaleTimeString()} · `}
                        {r.clicked_at && `Clicked ${new Date(r.clicked_at).toLocaleTimeString()}`}
                      </p>
                    </div>
                    <Badge className={s.class}>{s.label}</Badge>
                  </div>
                );
              })}
            </div>
          )}

          {/* iter249: pagination footer — server-side so we can browse the
              full recipient set instead of capping at the old client 1000. */}
          {recipTotal > 0 && (
            <div className="flex items-center justify-between mt-3 text-xs text-slate-500" data-testid="recipients-pagination">
              <span>Showing <strong>{startIdx.toLocaleString()}–{endIdx.toLocaleString()}</strong> of <strong>{recipTotal.toLocaleString()}</strong></span>
              <div className="flex items-center gap-2">
                <span>Per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="border rounded px-1 py-0.5 text-xs bg-white"
                  data-testid="recipients-page-size"
                >
                  {[25, 50, 100, 250, 500].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <Button variant="outline" size="sm" disabled={pageIndex === 0 || recipLoading} onClick={() => setPageIndex((p) => Math.max(0, p - 1))} data-testid="recipients-prev-page">Prev</Button>
                <span data-testid="recipients-page-indicator">Page {pageIndex + 1} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={(pageIndex + 1) >= totalPages || recipLoading} onClick={() => setPageIndex((p) => p + 1)} data-testid="recipients-next-page">Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showBody} onOpenChange={setShowBody}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Email Body</DialogTitle>
            <DialogDescription>This is the rendered HTML that was sent to recipients. The CAN-SPAM unsubscribe footer was appended at send time per-recipient (not shown here).</DialogDescription>
          </DialogHeader>
          <iframe
            title="campaign-body"
            srcDoc={campaign.html_body || '<p style="color:#94a3b8;padding:20px;font-family:sans-serif;">Body unavailable.</p>'}
            style={{ width: '100%', height: '60vh', border: '1px solid #e5e7eb', borderRadius: 4 }}
            data-testid="campaign-body-iframe"
          />
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
}
